import { useEffect, useRef } from 'react';

import Highcharts from 'highcharts';
import type { HighchartsReactRefObject } from 'highcharts-react-official';
import HighchartsReact from 'highcharts-react-official';

import { categoricalPallete } from '../themes/colors';

type TPillChartOptions = {
  colors?: string[];
  type?: string;
  yAxisTitle?: string;
  xAxisCategories?: string[];
  yAxisMax?: number;
  yAxisMin?: number;
  height?: string | number;
  tooltipFormatter?: (this: any) => string;
  onClick?: (e: any) => void;
  yAxisFormatter?: (this: any) => string;
  dataLabelsFormatter?: (this: any) => string;
};

export const PillChart = ({
  data,
  chartOptions,
}: {
  data: { name: string; data: number; color?: string }[];
  chartOptions: TPillChartOptions;
}) => {
  const chartRef = useRef<HighchartsReactRefObject>(null);
  const displayData = data.map((item, index) => {
    return {
      ...item,
      data: index === data.length - 1 ? [item.data * -1] : [item.data],
    };
  });

  const options = {
    chart: {
      type: chartOptions.type ?? 'bar',
      style: {
        fontFamily: 'Inter',
      },
      backgroundColor: 'transparent',
      height: chartOptions.height,
      margin: [undefined, 0, undefined, 0],
    },
    legend: {
      enabled: false,
    },
    title: {
      text: '',
      align: 'left',
      style: {
        fontFamily: 'Inter',
      },
    },
    xAxis: {
      categories: chartOptions.xAxisCategories,
      labels: {
        enabled: false,
      },
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
    },
    yAxis: {
      max:
        chartOptions.yAxisMax ??
        displayData
          .filter((item) => item.data?.some((d) => d > 0))
          .reduce((acc, item) => acc + Math.max(...item.data), 0),
      min:
        chartOptions.yAxisMin ??
        displayData
          .filter((item) => item.data?.some((d) => d < 0))
          .reduce((acc, item) => acc + Math.min(...item.data), 0),
      startOnTick: false,
      endOnTick: false,
      title: {
        text: chartOptions.yAxisTitle,
      },
      gridLineWidth: 0,
      labels: {
        y: 15,
        enabled: true,
        step: 0,
        formatter: chartOptions.yAxisFormatter,
        rotation: 0,
        overflow: 'justify',
        style: {
          fontFamily: 'Inter',
        },
        useHTML: true,
      },
    },
    colors: chartOptions.colors ?? categoricalPallete,
    plotOptions: {
      series: {
        stacking: 'normal',
        events: {
          click: chartOptions.onClick,
        },
      },
      bar: {
        groupPadding: 0,
        dataLabels: {
          align: 'left',
          enabled: chartOptions.dataLabelsFormatter !== undefined,
          formatter: chartOptions.dataLabelsFormatter,
          style: {
            fontWeight: '300',
            fontSize: '18px',
            fontFamily: 'Inter',
            padding: 1,
          },
          useHTML: true,
        },
        borderRadius: 8,
      },
    },
    tooltip: {
      formatter: chartOptions.tooltipFormatter,
    },
    series: displayData,
    credits: {
      enabled: false,
    },
  };

  useEffect(() => {
    if (chartRef.current?.container.current) {
      chartRef.current.container.current.style.width = '100%';
    }
    chartRef.current?.chart.reflow();
  }, []);

  return (
    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
  );
};
